import React, { useCallback, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardContent,
  Container,
  FormControl,
  TextField,
  Typography,
} from "@material-ui/core";
import { useBmapi } from "../../utils/bmapi-context";
import Title from "../../ui/Title";
import InfoIcon from "@material-ui/icons/Info";
import UserInspectionPlays from "../../components/UserInspectionPlays";
import UserInspectionProducts from "../../components/UserInspectionProducts";
import UserInspectionFidelityCards from "../../components/UserInspectionFidelityCards";
import { fromBmarkenDateToDay } from "../../utils/utils";
import UserInspectionConsentsDialog from "../../components/UserInspectionConsentsDialog";
import UserInspectionTransactionsDialog from "../../components/UserInspectionTransactionsDialog";

export default function UserInspection() {
  const { bmapi, startLoading, stopLoading } = useBmapi();

  const [email, setEmail] = useState("");
  const [userDetails, setUserDetails] = useState({
    id: null,
    complete_name: "-",
    mobile: "000-0000000",
    birthDate: "dd/MM/yyyy",
    language: "-",
  });

  const [userConsents, setUserConsents] = useState([]);
  const [userProducts, setUserProducts] = useState([]);

  // dialogs
  const [consentsDialogOpen, setConsentsDialogOpen] = useState(false);
  const [transactionsDialogOpen, setTransactionsDialogOpen] = useState(false);
  const [selectedConsents, setSelectedConsents] = useState([]);
  const [selectedTransactions, setSelectedTransactions] = useState([]);

  const handleChange = (event) => {
    setEmail(event.target.value.trim());
  };

  const loadUserConsents = useCallback(
    (userId) => {
      return bmapi.getConsentInfo(userId).then((values) => {
        const consents = values.consents;
        const consentExpresssions = values.consent_expressions;

        consentExpresssions.forEach(
          (element) =>
            (element["consent"] = consents.find(
              (x) => x.id === element.consent_id
            ))
        );

        consentExpresssions.sort(
          (a, b) =>
            new Date(b.signed_on).getTime() - new Date(a.signed_on).getTime()
        );

        setUserConsents(consentExpresssions);
      });
    },
    [bmapi]
  );

  // ✅ RAW: niente stopLoading/catch qui
  const loadUserProductsRaw = useCallback(
    (userId) => {
      const params = { user: userId };
      return bmapi.getUserInspectionProducts(null, params).then((values) => {
        setUserProducts(values || []);
      });
    },
    [bmapi]
  );

  // ✅ refresh affidabile
  const refreshProducts = useCallback(async () => {
    if (!userDetails?.id) return;
    startLoading();
    try {
      await loadUserProductsRaw(userDetails.id);
    } finally {
      stopLoading();
    }
  }, [userDetails?.id, startLoading, stopLoading, loadUserProductsRaw]);

  const getUser = useCallback(
    (data) => {
      startLoading();
      return bmapi
        .getUser(data)
        .then(async (values) => {
          const userData = Object.assign({}, userDetails);
          userData["complete_name"] = values.complete_name;
          userData["surname"] = values.last_name;
          userData["id"] = values.id;
          userData["mobile"] = values.mobile;
          userData["birthDate"] = values.birthday;
          userData["language"] = values.language;

          setUserDetails(userData);

          await Promise.all([
            loadUserConsents(values.id),
            loadUserProductsRaw(values.id),
          ]);
        })
        .catch((e) => {
          console.log("getUser error", e);
        })
        .finally(stopLoading);
    },
    [
      bmapi,
      userDetails,
      startLoading,
      stopLoading,
      loadUserConsents,
      loadUserProductsRaw,
    ]
  );

  const handleSubmit = () => {
    getUser(email);
  };

  const openConsentsDialog = (consent_id) => {
    startLoading();
    return bmapi
      .getConsents()
      .then((values) => {
        const found = values.find((x) => x.id === consent_id);
        setSelectedConsents(found ? [found] : []);
        setConsentsDialogOpen(true);
      })
      .finally(stopLoading)
      .catch((e) => {
        console.log("openConsentsDialog error", e);
        stopLoading();
      });
  };

  const openTransactionsDialog = (productId) => {
    startLoading();
    const query = { userId: userDetails.id, productId: productId };
    return bmapi
      .getTransactions(query)
      .then((values) => {
        setSelectedTransactions(values || []);
        setTransactionsDialogOpen(true);
      })
      .finally(stopLoading)
      .catch((e) => {
        console.log("openTransactionsDialog error", e);
        stopLoading();
      });
  };

  // Split per sezioni
  const plays = userProducts.filter((p) => p.type === "CAMPAIGN_MULTIWIN");
  const fidelityCards = userProducts.filter(
    (p) => p.type === "CAMPAIGN_EARNING_CARD"
  );
  const products = userProducts.filter(
    (p) => p.type !== "CAMPAIGN_MULTIWIN" && p.type !== "CAMPAIGN_EARNING_CARD"
  );

  const Field = ({ label, value }) => (
    <Box display="flex" alignItems="baseline" style={{ minWidth: 0 }}>
      <Typography variant="h6" style={{ marginRight: 8, whiteSpace: "nowrap" }}>
        {label}
      </Typography>
      <Typography
        style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
      >
        {value}
      </Typography>
    </Box>
  );

  return (
    <Container maxWidth="lg">
      <Title>Ispezione Utente</Title>

      <Box mt={3}>
        <Card>
          <CardContent>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <div style={{ flex: 1 }}>
                <FormControl required fullWidth>
                  <TextField
                    id="outlined-name"
                    label="Indirizzo email"
                    value={email}
                    onChange={handleChange}
                    fullWidth
                  />
                </FormControl>
              </div>
              <Button variant="outlined" onClick={handleSubmit}>
                Cerca
              </Button>
            </div>
          </CardContent>
        </Card>
      </Box>

      {userDetails.complete_name !== "-" ? (
        <Box mt={3}>
          <Card>
            <CardHeader title={"Dati utente"} />
            <CardContent>
              <Box
                display="grid"
                gridTemplateColumns="1fr 1fr"
                gridColumnGap={24}
                gridRowGap={12}
              >
                <Field label="Email:" value={email} />
                <Field label="Lingua:" value={userDetails.language} />

                <Field label="Nome:" value={userDetails.complete_name} />
                <Field label="Cognome:" value={userDetails.surname} />

                <Field label="Mobile:" value={userDetails.mobile} />
                <Field label="Data di nascita:" value={userDetails.birthDate} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      ) : null}

      {userConsents.length !== 0 ? (
        <Box mt={3}>
          <Card>
            <CardHeader title={"Consensi"} />
            <CardContent>
              <div style={{ width: "100%", overflowX: "auto" }}>
                <table style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th>
                        <Typography variant="h6">Tipo</Typography>
                      </th>
                      <th>
                        <Typography variant="h6">Data</Typography>
                      </th>
                      <th>
                        <Typography variant="h6">Valore</Typography>
                      </th>
                      <th>
                        <Typography variant="h6">Stato</Typography>
                      </th>
                      <th>
                        <Typography variant="h6">Campagna</Typography>
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {userConsents.map((k, i) => (
                      <tr key={i}>
                        <td>{k.consent?.type}</td>
                        <td>{fromBmarkenDateToDay(k.signed_on)}</td>
                        <td>{k.value}</td>
                        <td>{k.status}</td>
                        <td>{k.campaign_id}</td>
                        <td>
                          <InfoIcon
                            color="primary"
                            onClick={() => openConsentsDialog(k.consent_id)}
                            style={{ cursor: "pointer" }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </Box>
      ) : null}

      {fidelityCards.length !== 0 ? (
        <Box mt={3}>
          <Card>
            <CardHeader title={"Fidelity card"} />
            <CardContent>
              <UserInspectionFidelityCards
                cards={fidelityCards}
                onShowTransactions={(productId) =>
                  openTransactionsDialog(productId)
                }
              />
            </CardContent>
          </Card>
        </Box>
      ) : null}

      {products.length !== 0 ? (
        <Box mt={3}>
          <Card>
            <CardHeader title={"Prodotti"} />
            <CardContent>
              <UserInspectionProducts
                products={products}
                onShowTransactions={(productId) =>
                  openTransactionsDialog(productId)
                }
                onExpiryDateUpdated={refreshProducts}
              />
            </CardContent>
          </Card>
        </Box>
      ) : null}

      {plays.length !== 0 ? (
        <Box mt={3}>
          <Card>
            <CardHeader title={"Giocate"} />
            <CardContent>
              <UserInspectionPlays
                plays={plays}
                onShowTransactions={(productId) =>
                  openTransactionsDialog(productId)
                }
              />
            </CardContent>
          </Card>
        </Box>
      ) : null}

      <UserInspectionConsentsDialog
        open={consentsDialogOpen}
        onClose={() => setConsentsDialogOpen(false)}
        consents={selectedConsents}
      />

      <UserInspectionTransactionsDialog
        open={transactionsDialogOpen}
        onClose={() => setTransactionsDialogOpen(false)}
        transactions={selectedTransactions}
        canReverseTransaction={true}
        onReversed={async () => {
          setTransactionsDialogOpen(false);
          await refreshProducts();
        }}
      />
    </Container>
  );
}
